/**
 * Mini-Cart-State im localStorage — pro Subdomain (= pro Agent) gespeichert.
 *
 * Items sind clientseitig persistiert, server-side nur wenn der User auf
 * "Checkout" klickt → erzeugt einen Cart-Token via Backend.
 */
import { useEffect, useState } from "react";

export type CartItem = {
  wc_product_id: number;
  qty: number;
  variation_id?: number | null;
  variation_label?: string;  // z.B. "Mango • 20mg"
  // Snapshot fuer Anzeige im Drawer (kann sich aendern, ist nur UI)
  name: string;
  price: string | null;
  image_url: string | null;
};

const KEY_PREFIX = "dp-cart-v1::";

function keyFor(agentCode: string): string {
  return `${KEY_PREFIX}${agentCode.toLowerCase()}`;
}

function load(agentCode: string): CartItem[] {
  try {
    const raw = localStorage.getItem(keyFor(agentCode));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (it) => it && typeof it.wc_product_id === "number" && typeof it.qty === "number",
    );
  } catch {
    return [];
  }
}

function save(agentCode: string, items: CartItem[]): void {
  try {
    localStorage.setItem(keyFor(agentCode), JSON.stringify(items));
    window.dispatchEvent(new CustomEvent("dp-cart-changed", { detail: { agentCode } }));
  } catch {
    /* quota? ignore */
  }
}

export function getCartItems(agentCode: string): CartItem[] {
  return load(agentCode);
}

export function getCartCount(agentCode: string): number {
  return load(agentCode).reduce((sum, it) => sum + (it.qty || 0), 0);
}

export function addToCart(agentCode: string, item: CartItem): CartItem[] {
  const items = load(agentCode);
  // Match auf product_id + variation_id (null = simple-Produkt)
  const idx = items.findIndex(
    (it) =>
      it.wc_product_id === item.wc_product_id &&
      (it.variation_id ?? null) === (item.variation_id ?? null),
  );
  if (idx >= 0) {
    items[idx] = {
      ...items[idx],
      qty: Math.min(99, items[idx].qty + item.qty),
      // refresh snapshot
      name: item.name,
      price: item.price,
      image_url: item.image_url,
      variation_label: item.variation_label,
    };
  } else {
    items.push({ ...item, qty: Math.max(1, Math.min(99, item.qty)) });
  }
  save(agentCode, items);
  return items;
}

export function updateQty(
  agentCode: string,
  productId: number,
  variationId: number | null | undefined,
  qty: number,
): CartItem[] {
  const items = load(agentCode);
  const idx = items.findIndex(
    (it) =>
      it.wc_product_id === productId &&
      (it.variation_id ?? null) === (variationId ?? null),
  );
  if (idx >= 0) {
    if (qty <= 0) {
      items.splice(idx, 1);
    } else {
      items[idx] = { ...items[idx], qty: Math.min(99, qty) };
    }
    save(agentCode, items);
  }
  return items;
}

export function removeItem(
  agentCode: string,
  productId: number,
  variationId: number | null | undefined,
): CartItem[] {
  return updateQty(agentCode, productId, variationId, 0);
}

export function clearCart(agentCode: string): void {
  save(agentCode, []);
}

/**
 * React-Hook: synchron mit localStorage + custom events fuer Multi-Komponenten-Sync.
 */
export function useCart(agentCode: string) {
  const [items, setItems] = useState<CartItem[]>(() => load(agentCode));
  useEffect(() => {
    setItems(load(agentCode));
    const onChange = (e: Event) => {
      const ev = e as CustomEvent<{ agentCode: string }>;
      if (ev.detail?.agentCode === agentCode.toLowerCase()) {
        setItems(load(agentCode));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === keyFor(agentCode)) {
        setItems(load(agentCode));
      }
    };
    window.addEventListener("dp-cart-changed", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("dp-cart-changed", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [agentCode]);
  const count = items.reduce((sum, it) => sum + (it.qty || 0), 0);
  return {
    items,
    count,
    add: (it: CartItem) => setItems(addToCart(agentCode, it)),
    update: (productId: number, variationId: number | null | undefined, qty: number) =>
      setItems(updateQty(agentCode, productId, variationId, qty)),
    remove: (productId: number, variationId: number | null | undefined) =>
      setItems(removeItem(agentCode, productId, variationId)),
    clear: () => {
      clearCart(agentCode);
      setItems([]);
    },
  };
}
